import React, { FC, useCallback, useState } from 'react';
import { FormikHelpers } from 'formik';
import { TokenPayload } from '@recurly/recurly-js';
import { useTranslation } from '@blocs.i18n';
import { WindowWidthContext } from '@minecraft.utils';
import { BasicContainer, FlexCol, FlexDynamic, H1Text, H2Text, Loader } from '@minecraft.atoms';
import Modal, { useModal } from '@minecraft.modal';
import Loading from '@minecraft.loader';
import { PaymentForm } from '../Checkout/PaymentForm';
import { SavedPaymentMethodSummary } from '../Checkout/PaymentForm/SavedPaymentMethodSummary';
import { PaymentMethodFormType } from '../Checkout/types';
import { AddCard, CreditCard } from '../CreditCard';
import { useUserContext } from '../../Auth';
import { useBillingPopup } from '../BillingPopupContext';
import { ThreeDActionType, ThreeDSecureForm, useThreeDSecureFlow } from '../ThreeDSecureFlow';
import { AddressValidationForm, applySuggestedAddressToFormik, useAddressValidator } from '../AddressValidation';
import { getNewPaymentMethodPriority, getPaymentMethodPriorities, usePaymentMethodForm } from '../usePaymentMethodForm';
import { UseBilling } from '../useBilling';

export type PaymentMethodsManagerProps = {
  title?: string;
  isUpdateBillingInfo?: boolean;
  isAddModal?: boolean;
  className?: string;
  showFormBorder?: boolean;
  onCardAdded?: () => void;
};

enum PaymentViewMode {
  VIEW = 'VIEW',
  EDIT = 'EDIT',
  ADD = 'ADD',
}

type SelectedCard = UseBilling['wallet'][0];

export const PaymentMethodsManager: FC<PaymentMethodsManagerProps> = ({
  title,
  isUpdateBillingInfo = false,
  isAddModal = false,
  className,
  showFormBorder,
  onCardAdded,
}): JSX.Element => {
  const { t } = useTranslation();
  const isMobile = WindowWidthContext.useIsMobile();

  const { isOpen: removeModalOpen, toggleModal: toggleRemovePaymentModal } = useModal(false);
  const [selectedCard, setSelectedCard] = useState<SelectedCard>(null);
  // undefined until the user picks explicitly — displayedViewMode below supplies the default.
  const [viewMode, setViewMode] = useState<PaymentViewMode>(isAddModal ? PaymentViewMode.ADD : undefined);

  const { myAccount, subscription } = useUserContext();
  const countryCode = myAccount?.country?.code;
  const currencyCode = subscription?.plan?.currency?.code;

  const {
    onAddPaymentOption,
    addPaymentLoading,
    addPaymentError,
    onRemovePaymentOption,
    removePaymentLoading,
    onUpdatePaymentOption,
    updatePaymentLoading,
    wallet,
    walletLoading,
    setVisibleError,
    getCountryName,
  } = useBillingPopup();

  const isWalletEmpty = wallet?.length === 0;
  // Nothing saved yet to show a view-mode summary of — default straight to the add-card form.
  const displayedViewMode = viewMode ?? (isWalletEmpty ? PaymentViewMode.ADD : PaymentViewMode.VIEW);
  const isEditing = displayedViewMode === PaymentViewMode.EDIT;
  const isAdding = displayedViewMode === PaymentViewMode.ADD;
  const displayedCard = selectedCard ?? wallet?.find((card) => card.isPrimaryPaymentMethod) ?? wallet?.[0] ?? null;
  const countryName = getCountryName(displayedCard?.country);

  // useThreeDSecureFlow/usePaymentMethodForm only need to know which card is now selected — the
  // add/update-success view-mode transition is handled explicitly below instead, since setSelectedCard
  // here is also called for priority-only updates (handlePaymentPriorityChange), which must NOT kick
  // the user out of whatever form they have open.
  const {
    threeDActionContext,
    onAddPaymentOptionWith3DS,
    onUpdatePaymentOptionWith3DS,
    onThreeDActionToken,
    onThreeDActionError,
  } = useThreeDSecureFlow({
    onAddPaymentOption,
    onUpdatePaymentOption,
    setSelectedCard,
    setVisibleError,
  });

  const {
    paymentMethodFormRef,
    paymentMethodFormikRef,
    handlePaymentPriorityChange,
    requestRecurlyTokenAndValidate: tokenizeAndSubmit,
  } = usePaymentMethodForm({
    onUpdatePaymentOption: onUpdatePaymentOptionWith3DS,
    setSelectedCard,
  });

  const { checkAddress, pendingAddressIssue, resolveAddressIssue, dismissAddressIssue } = useAddressValidator({
    onAddressCorrected: (address) => applySuggestedAddressToFormik(paymentMethodFormikRef.current, address),
  });

  const handleAddSuccess = useCallback(() => {
    if (isAddModal) {
      onCardAdded?.();
    } else {
      setViewMode(PaymentViewMode.VIEW);
    }
  }, [isAddModal, onCardAdded]);

  // Shared by both the "add" branch of the Recurly submit handler and PayPal — adding a card always
  // means the same thing here regardless of how the token was obtained.
  const addNewPaymentMethod = useCallback(
    async (token: TokenPayload, formValues: PaymentMethodFormType) => {
      const { isBackupPaymentMethod, isPrimaryPaymentMethod } = getNewPaymentMethodPriority(formValues, isWalletEmpty);
      const addedCard = await onAddPaymentOptionWith3DS({ token, isBackupPaymentMethod, isPrimaryPaymentMethod });
      if (addedCard) handleAddSuccess();
    },
    [onAddPaymentOptionWith3DS, isWalletEmpty, handleAddSuccess]
  );

  const handleWalletFormSubmit = useCallback(
    async (formData: PaymentMethodFormType, formikHelpers: FormikHelpers<PaymentMethodFormType>) => {
      const accepted = await checkAddress(formData);
      if (!accepted) return;
      tokenizeAndSubmit(formData, formikHelpers, async (token) => {
        if (formData.billingInfoId) {
          const { isBackupPaymentMethod, isPrimaryPaymentMethod } = getPaymentMethodPriorities(formData);
          // After a successful update, leave EDIT mode and show the read-only card view for the
          // updated card instead of leaving the (now stale) edit form open.
          const updatedCard = await onUpdatePaymentOptionWith3DS({
            billingInfoId: formData.billingInfoId,
            token,
            isBackupPaymentMethod,
            isPrimaryPaymentMethod,
          });
          if (updatedCard) setViewMode(PaymentViewMode.VIEW);
        } else {
          await addNewPaymentMethod(token, formData);
        }
      });
    },
    [checkAddress, tokenizeAndSubmit, onUpdatePaymentOptionWith3DS, addNewPaymentMethod]
  );

  // PayPal always represents adding a new payment method here, even while editing an existing card —
  // there's no "replace this card's token in place" concept for PayPal the way there is for Recurly
  // hosted card fields.
  const handleWalletPayPalToken = useCallback(
    (token: TokenPayload) => addNewPaymentMethod(token, paymentMethodFormikRef.current?.values),
    [addNewPaymentMethod, paymentMethodFormikRef]
  );

  // The 3D-Secure-confirmed completion of either flow above resolves through Recurly's own onToken
  // callback rather than through handleWalletFormSubmit/handleWalletPayPalToken, so it needs the same
  // transition applied separately here — only once onThreeDActionToken reports the mutation actually
  // completed (a `card` result), not on failure or a second 3DS round.
  const handleThreeDActionToken = useCallback(
    async (token: TokenPayload) => {
      const { actionType, card } = (await onThreeDActionToken(token)) ?? {};
      if (!card) return;
      if (actionType === ThreeDActionType.ADD_PAYMENT) {
        handleAddSuccess();
      } else if (actionType === ThreeDActionType.UPDATE_PAYMENT) {
        setViewMode(PaymentViewMode.VIEW);
      }
    },
    [onThreeDActionToken, handleAddSuccess]
  );

  const sharedPaymentFormProps = {
    mode: 'wallet' as const,
    wallet,
    onSubmit: handleWalletFormSubmit,
    onPayPalToken: handleWalletPayPalToken,
    addPaymentError,
    onCancel: () => null,
    threeDActionContext,
    countryCode,
    currencyCode,
    addPaymentLoading,
    paymentMethodFormRef,
    paymentMethodFormikRef,
    showApplePayButton: false,
    onClickApplePay: () => null,
    allowCancel: false,
    showAuthorizeMessage: true,
    w: isMobile || isAddModal ? 'full' : '2/3',
    bgColor: 'card' as const,
    showFormBorder,
  };

  return (
    <>
      <Loader
        loading={walletLoading || updatePaymentLoading || addPaymentLoading || Boolean(threeDActionContext?.token)}
        LoaderComponent={<Loading />}
      >
        <BasicContainer className={className} m="4">
          {!isMobile && title && <H1Text>{title}</H1Text>}
          <FlexDynamic
            direction="column"
            alignItems="start"
            justifyContent="center"
            gap="4"
            mdProps={{
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              direction: 'row',
              wrap: 'nowrap',
              gap: '12',
            }}
            data-testid="wallet-container"
          >
            {!isAddModal && (
              <FlexCol gap="2" w="full" mdProps={{ className: 'cn_atom_pr-8', w: '2/3' }}>
                {isUpdateBillingInfo && (
                  <BasicContainer my="4">
                    <H2Text>{t('common:billing.wallet')}</H2Text>
                  </BasicContainer>
                )}
                {wallet?.map((card) => (
                  <CreditCard
                    isSelected={!isAdding && card.id === displayedCard?.id}
                    isPrimary={card.isPrimaryPaymentMethod}
                    isBackup={card.isBackupPaymentMethod}
                    cardType={card.cardType}
                    paymentType={card.paymentType}
                    lastFour={card.lastFour}
                    onSelect={() => {
                      setSelectedCard(card);
                      setViewMode(PaymentViewMode.VIEW);
                    }}
                    key={card.id}
                  />
                ))}
                <AddCard onSelect={() => setViewMode(PaymentViewMode.ADD)} />
              </FlexCol>
            )}
            {isEditing && (
              <PaymentForm
                {...sharedPaymentFormProps}
                selectedCard={displayedCard}
                setSelectedCard={setSelectedCard}
                handlePaymentPriorityChange={handlePaymentPriorityChange}
                ctaLabel={t('common:button.update')}
                onCancel={() => setViewMode(PaymentViewMode.VIEW)}
                allowCancel
              />
            )}
            {isAdding && (
              <PaymentForm
                {...sharedPaymentFormProps}
                selectedCard={null}
                setSelectedCard={setSelectedCard}
                ctaLabel={t('common:button.add')}
              />
            )}
            {!isEditing && !isAdding && (
              <SavedPaymentMethodSummary
                selectedCard={displayedCard}
                onEditClick={() => setViewMode(PaymentViewMode.EDIT)}
                handlePaymentPriorityChange={handlePaymentPriorityChange}
                toggleRemovePaymentModal={toggleRemovePaymentModal}
                disabled={addPaymentLoading || Boolean(threeDActionContext?.token)}
                countryName={countryName}
              />
            )}
          </FlexDynamic>
          <Modal
            data-testid="remove-billing-info-modal"
            isOpen={removeModalOpen}
            modalSize="small"
            cancelLabel={t('common:button.cancel')}
            submitLabel={t('common:button.confirm')}
            handleSubmitClick={async () => {
              await onRemovePaymentOption({ billingInfoId: displayedCard?.id?.toString() });
              setSelectedCard(null);
              toggleRemovePaymentModal();
            }}
            handleToggleClick={() => toggleRemovePaymentModal()}
            title={t('common:billing.card.delete.title')}
            overlayLoading={removePaymentLoading}
          />
        </BasicContainer>
      </Loader>
      {threeDActionContext?.token && (
        <ThreeDSecureForm
          threeDActionToken={threeDActionContext.token}
          onToken={handleThreeDActionToken}
          onError={onThreeDActionError}
          loading={addPaymentLoading}
        />
      )}
      {pendingAddressIssue && (
        <AddressValidationForm
          result={pendingAddressIssue.result}
          currentAddress={pendingAddressIssue.currentAddress}
          lastCheckedInput={pendingAddressIssue.lastCheckedInput}
          onSubmit={resolveAddressIssue}
          onDismiss={dismissAddressIssue}
        />
      )}
    </>
  );
};
